// Status groups — how a workflow status reads at a glance (task 355.3).
//
// Six groups, each with one semantic colour in the Console Glass palette:
//   DRAFT (slate) · IN_FLIGHT (sky, pulsing) · NEEDS_ACTION (amber) · READY (teal) · DONE (mint) · FAILED (rose)
//
// The authoritative mapping is per workflow state and comes from the api (task 355.10: a display group on
// each workflow state). Until a document's state is found there, this client map is the fallback, and an
// unknown status resolves to null — rendered neutral, never guessed.

export type StatusGroup = 'DRAFT' | 'IN_FLIGHT' | 'NEEDS_ACTION' | 'READY' | 'DONE' | 'FAILED';

export const STATUS_GROUPS: readonly StatusGroup[] = [
  'DRAFT',
  'IN_FLIGHT',
  'NEEDS_ACTION',
  'READY',
  'DONE',
  'FAILED',
];

/** Fallback by status code, for states the api has not (yet) classified. */
export const FALLBACK_STATUS_GROUPS: Readonly<Record<string, StatusGroup>> = {
  DRAFT: 'DRAFT',
  NEW: 'DRAFT',
  CANCELLED: 'DRAFT',
  SUBMITTED: 'IN_FLIGHT',
  SENT: 'IN_FLIGHT',
  PROCESSING: 'IN_FLIGHT',
  CREATING_TRANSACTION: 'IN_FLIGHT',
  ACCEPTED_BY_PROVIDER: 'IN_FLIGHT',
  UNDER_REVIEW: 'NEEDS_ACTION',
  PENDING_APPROVAL: 'NEEDS_ACTION',
  PENDING_SETTLEMENT: 'NEEDS_ACTION',
  // 401.9 / 401.10 manual records awaiting the confirming person, and the rejected ones (api V454 / V455).
  PENDING_MANUAL_APPROVAL: 'NEEDS_ACTION',
  // 401.10 K1: a manual designated (CASS 7) allocation awaiting compliance (api V458).
  PENDING_MANUAL_DESIGNATION: 'NEEDS_ACTION',
  APPROVED: 'READY',
  SETTLED: 'DONE',
  COMPLETED: 'DONE',
  ACTIVE: 'DONE',
  REJECTED: 'FAILED',
  MANUAL_REJECTED: 'FAILED',
  FAILED: 'FAILED',
  RETURNED: 'FAILED',
  ERROR: 'FAILED',
};

/** Key of the api map: one entry per (workflow, state). */
export function statusGroupKey(workflowCode: string, stateCode: string): string {
  return `${workflowCode}:${stateCode}`;
}

/**
 * Resolves a status to its group.
 *  1. the api map for (workflowCode, status), when both are known;
 *  2. without a workflow code, the api map only if every workflow that has this state agrees;
 *  3. the client fallback by status code;
 *  4. null (neutral).
 */
export function resolveStatusGroup(
  status: string | null | undefined,
  workflowCode?: string | null,
  apiMap?: ReadonlyMap<string, StatusGroup>,
): StatusGroup | null {
  if (!status) return null;

  if (apiMap && apiMap.size > 0) {
    if (workflowCode) {
      const exact = apiMap.get(statusGroupKey(workflowCode, status));
      if (exact) return exact;
    } else {
      const suffix = `:${status}`;
      const groups = new Set<StatusGroup>();
      for (const [key, group] of apiMap) if (key.endsWith(suffix)) groups.add(group);
      if (groups.size === 1) return [...groups][0];
    }
  }

  return FALLBACK_STATUS_GROUPS[status] ?? null;
}

/** 'PENDING_SETTLEMENT' → 'PENDING SETTLEMENT' */
export function statusLabel(status: string | null | undefined): string {
  return (status ?? '').replace(/_/g, ' ');
}
